import {
  Alert,
  Box,
  Button,
  FileButton,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PiPaperclip } from 'react-icons/pi';
import {
  deliveryErrorText,
  NOT_PAID_REASONS,
  type PortfolioService,
  RENEWAL_MONTHS,
  requirementLabel,
  useDeliverService,
} from '@/models/clientCompanies';
import classes from '@/components/client-portfolio/DeliverAction.module.css';

const COUNTDOWN_SECONDS = 10;

export type PortfolioOwner =
  | {
      kind: 'company';
      registrationDate: string | null;
      missing: string[];
    }
  | { kind: 'contact' };

type Draft = {
  periodStart: string | null;
  comment: string;
  files: File[];
};

export function DeliverAction({
  service,
  owner,
}: {
  service: PortfolioService;
  owner: PortfolioOwner;
}) {
  const deliver = useDeliverService();
  const [open, setOpen] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const timer = useRef<number>(undefined);
  const tick = useRef<number>(undefined);

  const stop = useCallback(() => {
    clearTimeout(timer.current);
    clearInterval(tick.current);
    setSeconds(0);
  }, []);

  useEffect(() => stop, [stop]);

  if (service.deliveryState !== 'pending') return null;

  const missing = owner.kind === 'company' ? owner.missing : [];
  const withCycle = service.origin === 'offer' && service.kind === 'recurring';

  const start = (draft: Draft) => {
    setOpen(false);
    setSeconds(COUNTDOWN_SECONDS);
    tick.current = window.setInterval(() => setSeconds((s) => s - 1), 1000);
    timer.current = window.setTimeout(() => {
      stop();
      deliver
        .mutateAsync({
          serviceId: Number(service.id),
          periodStart: withCycle ? draft.periodStart : null,
          comment: draft.comment,
          files: draft.files,
        })
        .then((r) =>
          notifications.show({
            color: 'green',
            title: 'Delivered',
            message: r.cycle
              ? `${service.title} - next renewal ${dayjs(r.cycle.renewalDate).format('DD.MM.YYYY')}`
              : service.title,
          }),
        )
        .catch((e) =>
          notifications.show({
            color: 'red',
            title: 'Not marked delivered',
            message: deliveryErrorText(e),
            autoClose: false,
          }),
        );
    }, COUNTDOWN_SECONDS * 1000);
  };

  if (seconds > 0)
    return (
      <Button
        size="compact-xs"
        color="gray"
        variant="light"
        onClick={stop}
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        <Box className={classes.countdownOverlay} />
        Undo ({seconds}s)
      </Button>
    );

  if (service.origin === 'offer' && service.paid === false)
    return (
      <Tooltip
        multiline
        w={260}
        label={NOT_PAID_REASONS[service.paidState ?? ''] ?? 'Not paid yet'}
      >
        <Box>
          <Button size="compact-xs" variant="light" color="teal" disabled>
            Mark delivered
          </Button>
        </Box>
      </Tooltip>
    );

  if (missing.length > 0)
    return (
      <Tooltip
        multiline
        w={260}
        label={`Fill in the company data first: ${missing.map(requirementLabel).join(', ')}`}
      >
        <Stack gap={0} align="flex-end">
          <Button size="compact-xs" variant="light" color="teal" disabled>
            Mark delivered
          </Button>
          <Text fz={10} c="orange">
            {missing.length} company items missing
          </Text>
        </Stack>
      </Tooltip>
    );

  return (
    <>
      <Button
        size="compact-xs"
        variant="light"
        color="teal"
        onClick={() => setOpen(true)}
        loading={deliver.isPending}
      >
        Mark delivered
      </Button>
      {open && (
        <DeliverDialog
          service={service}
          withCycle={withCycle}
          defaultStart={
            owner.kind === 'company'
              ? owner.registrationDate
              : dayjs().format('YYYY-MM-DD')
          }
          onCancel={() => setOpen(false)}
          onConfirm={start}
        />
      )}
    </>
  );
}

function DeliverDialog({
  service,
  withCycle,
  defaultStart,
  onCancel,
  onConfirm,
}: {
  service: PortfolioService;
  withCycle: boolean;
  defaultStart: string | null;
  onCancel: () => void;
  onConfirm: (draft: Draft) => void;
}) {
  const [periodStart, setPeriodStart] = useState<string | null>(defaultStart);
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const months = RENEWAL_MONTHS[service.renewalPeriod ?? ''];
  const renewal =
    periodStart && months
      ? dayjs(periodStart).add(months, 'month').format('DD.MM.YYYY')
      : null;

  return (
    <Modal opened onClose={onCancel} title="Mark service delivered">
      <Stack gap="sm">
        <Text fw={500}>{service.title}</Text>
        <Textarea
          label="Comment"
          placeholder="Optional: what was delivered, information from the client"
          autosize
          minRows={2}
          maxLength={10000}
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
        />
        <Group gap="xs">
          <FileButton onChange={setFiles} multiple>
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="default"
                leftSection={<PiPaperclip size={14} />}
              >
                Attach files
              </Button>
            )}
          </FileButton>
          <Text fz="xs" c="dimmed">
            {files.length > 0
              ? files.map((f) => f.name).join(', ')
              : 'Optional'}
          </Text>
        </Group>
        {withCycle && (
          <DateInput
            label="Renewal cycle start"
            description="Defaults to the company registration date"
            valueFormat="DD.MM.YYYY"
            value={periodStart}
            onChange={setPeriodStart}
            withAsterisk
          />
        )}
        {withCycle && renewal && (
          <Text fz="xs" c="dimmed">
            Next renewal: {renewal}
          </Text>
        )}
        {!withCycle && (
          <Text fz="xs" c="dimmed">
            One-off service - no renewal cycle.
          </Text>
        )}
        <Alert color="gray" variant="light" p="xs">
          <Text fz="xs">
            Delivery cannot be undone after the 10 second countdown.
          </Text>
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color="teal"
            disabled={withCycle && !periodStart}
            onClick={() => onConfirm({ periodStart, comment, files })}
          >
            Mark delivered
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
